import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "@/lib/utils"

const Tabs = TabsPrimitive.Root

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "inline-flex h-12 items-center justify-center rounded-lg p-1 text-muted-foreground",
      // Liquid glass effect
      "bg-white/10 dark:bg-black/20 backdrop-blur-md",
      "border border-white/20 dark:border-white/10",
      "shadow-lg shadow-black/10",
      // Light theme adjustments
      "hover:bg-white/15 dark:hover:bg-black/30",
      "transition-all duration-300",
      className
    )}
    {...props}
  />
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium",
      "ring-offset-background transition-all duration-300",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      "disabled:pointer-events-none disabled:opacity-50",
      // Liquid glass - inactive state
      "bg-transparent text-foreground/70",
      "hover:bg-white/10 dark:hover:bg-white/5",
      "hover:text-foreground",
      // Liquid glass - active state
      "data-[state=active]:bg-white/20 dark:data-[state=active]:bg-white/10",
      "data-[state=active]:backdrop-blur-sm",
      "data-[state=active]:text-foreground",
      "data-[state=active]:shadow-lg data-[state=active]:shadow-black/10",
      "data-[state=active]:border data-[state=active]:border-white/30 dark:data-[state=active]:border-white/20",
      className
    )}
    {...props}
  />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
